import { useState } from "react";

import { compact, map } from "lodash/fp";
import { useNavigate, useParams } from "react-router-dom";

import { Stack } from "@mui/material";

import { TabContent } from "@app/analytics/account/WorkspaceSettings/components/styles";
import UpsellTooltipWrapper from "@app/analytics/components/UpsellTooltipWrapper";
import { HeadCell } from "@app/common/hooks/useDataTable";
import { showToast } from "@app/common/hooks/useToasts";
import ROUTES from "@app/common/routes";
import { pluralizePhrase } from "@app/common/utils";
import {
  EnterpriseRole,
  EnterpriseTeamUser,
  EnterpriseUser,
  EnterpriseWorkspaceInvite,
} from "@app/common/workspaces/types";
import {
  isWorkspaceAdmin,
  userIsTheOnlyTeamManager,
} from "@app/common/workspaces/workspaceUtils";

import InfoPanel from "@rui/InfoPanel";

import useWorkspace from "../../../../common/workspaces/useWorkspace";
import LeaveConfirmationModal from "../../../components/LeaveConfirmationModal";
import Breadcrumbs from "../components/Breadcrumb";
import MembersTable from "../components/MembersTable";
import WorkspaceActionButton from "../components/WorkspaceActionButton";
import WorkspaceHeader from "../components/WorkspaceHeader";
import AddTeamMembersModal from "../modals/AddTeamMembersModal";
import RemoveTeamMemberConfirmationModal from "../modals/RemoveTeamMemberConfirmationModal";

const HEAD_CELLS: HeadCell<
  EnterpriseUser | EnterpriseTeamUser | EnterpriseWorkspaceInvite
>[] = [
  {
    id: "name",
    label: "Name",
    colSpan: 2,
    sortable: true,
    sortBy: (memberOrInvite) =>
      memberOrInvite.type === "user"
        ? memberOrInvite.fullName
        : memberOrInvite.email,
  },
  {
    id: "teamRole",
    label: "Team Role",
    sortable: true,
    sortBy: (member) =>
      member.type === "user" && member.role === "manager" ? 1 : -1,
  },
  { id: "actions", label: "", sortable: false },
];

const TeamDetail = () => {
  const [addTeamMemberModalOpen, setAddTeamMemberModalOpen] = useState(false);
  const [leaveTeamModalOpen, setLeaveTeamModalOpen] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<{
    id: string;
    type: "user" | "invite";
  } | null>(null);

  const { teamId } = useParams();
  const navigate = useNavigate();

  const {
    workspace,
    removeUsersFromTeam,
    updateTeamUsers,
    workspaceIsInactive,
    workspaceUser,
  } = useWorkspace();

  const team = workspace.teams.find((team) => team.id === teamId)!;
  const teamRole = workspaceUser.teams?.find(
    (team) => team.id === teamId
  )?.role;

  const isAdmin = isWorkspaceAdmin(workspaceUser);
  const canEditTeam = teamRole === "manager" || isAdmin;

  if (!team) {
    return (
      <InfoPanel severity="error" fullWidth>
        Sorry, this team does not exist.
      </InfoPanel>
    );
  }

  const handleMemberRemoveConfirm = () => {
    // it shouldn't be possible for an invite to be selected from the
    // team detail page, but return early just in case
    if (memberToRemove == null || memberToRemove.type === "invite") return;

    removeUsersFromTeam.mutate(
      {
        teamId: teamId!,
        userIds: [memberToRemove.id],
      },
      {
        onSuccess: () => setMemberToRemove(null),
      }
    );
  };

  const handleLeaveTeamConfirm = () => {
    if (!workspaceUser.userId) return;

    removeUsersFromTeam.mutate(
      {
        teamId: teamId!,
        userIds: [workspaceUser.userId],
      },
      {
        onSuccess: () => navigate(ROUTES.WORKSPACE_SETTINGS_ROOT),
      }
    );
  };

  const handleMemberRemoveClick =
    (id: string, type: "user" | "invite") => () => {
      setMemberToRemove({ id, type });
    };

  const handleRoleChange = (userId: string) => (role: EnterpriseRole) => {
    if (
      role === "member" &&
      userIsTheOnlyTeamManager(userId, workspace, teamId)
    ) {
      return showToast({
        severity: "error",
        message:
          "You must appoint a new manager before demoting the current manager",
      });
    }

    return updateTeamUsers([{ userId, teamId: teamId!, role }]);
  };

  const handleRemoveMultipleTeamUsers = (
    rows: { id: string; type: "user" | "invite" }[],
    onSuccess: (failures: string[]) => void
  ) => {
    const userIds = map(
      "id",
      rows.filter((row) => row.type === "user")
    );

    removeUsersFromTeam.mutate(
      {
        teamId: teamId!,
        userIds,
      },
      { onSuccess }
    );
  };

  const allTeamMembers = [...team.users, ...team.invites];

  const breadcrumbItems = [
    {
      label: "Teams",
      link: ROUTES.workspaceSettingsById(workspace.id),
    },
    { label: team.name },
  ];

  return (
    <>
      <Stack flex={1}>
        <Breadcrumbs items={breadcrumbItems} />
        <WorkspaceHeader
          id={team.id}
          title={team.name}
          type="team"
          canEdit={canEditTeam}
          subtitle={`${pluralizePhrase(allTeamMembers.length, "team member")} ${
            team.invites?.length > 0 ? `(${team.invites.length} pending)` : ""
          }`}
          actions={
            canEditTeam
              ? compact([
                  <WorkspaceActionButton
                    text="Configure Team"
                    key="configure-team"
                    actionType="settings"
                    onClick={() => navigate("settings")}
                  />,
                  <UpsellTooltipWrapper
                    key="add-user"
                    show={workspaceIsInactive}
                  >
                    <WorkspaceActionButton
                      disabled={workspaceIsInactive}
                      text="Add Team Member"
                      onClick={() => setAddTeamMemberModalOpen(true)}
                    />
                  </UpsellTooltipWrapper>,
                ])
              : [
                  <WorkspaceActionButton
                    text="Leave this team"
                    key="leave-team"
                    actionType="warning"
                    color="error"
                    onClick={() => setLeaveTeamModalOpen(!leaveTeamModalOpen)}
                  />,
                ]
          }
          sx={{ mt: 2 }}
        />
        <TabContent>
          <MembersTable
            context="team"
            headCells={HEAD_CELLS}
            invites={team.invites}
            onMemberRemove={handleMemberRemoveClick}
            onRemoveMultiple={handleRemoveMultipleTeamUsers}
            onRoleChange={handleRoleChange}
            canEditTeam={canEditTeam}
            users={team.users}
          />
        </TabContent>
      </Stack>

      {/* Modals */}
      <AddTeamMembersModal
        team={team}
        open={addTeamMemberModalOpen}
        onClose={() => setAddTeamMemberModalOpen(false)}
      />
      <RemoveTeamMemberConfirmationModal
        loading={removeUsersFromTeam.isPending}
        open={memberToRemove != null}
        onClose={() => setMemberToRemove(null)}
        onConfirm={handleMemberRemoveConfirm}
      />
      <LeaveConfirmationModal
        loading={removeUsersFromTeam.isPending}
        open={leaveTeamModalOpen}
        onClose={() => setLeaveTeamModalOpen(false)}
        onConfirm={handleLeaveTeamConfirm}
      />
    </>
  );
};

export default TeamDetail;
